import { Card, CodeViewer, Flex, SimpleTable, Typography } from "djuno-design";

const { Title, Text } = Typography;

const UtilitiesPage = () => {
  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Flex direction="col">
          <Title level={4}>Utilities</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            This page contains a set of utility functions that provide common
            functionality such as managing class names, copying text to
            clipboard, handling local storage, generating unique identifiers,
            and more. Below is a list of the available utility functions:
          </Text>

          <Title level={4}>List of Utility Functions</Title>
          <ul className="marker:text-sky-400 list-decimal pl-6 mb-3">
            <li>
              <Text size="sm" code strong>
                cn:
              </Text>
              <Text size="sm">
                {` `} Combines class names with Tailwind CSS class merging
                support.
              </Text>
            </li>
            <li>
              <Text size="sm" code strong>
                copyToClipboard:
              </Text>
              <Text size="sm">
                {` `} Copies text to the clipboard with optional success and
                failure messages.
              </Text>
            </li>
            <li>
              <Text size="sm" code strong>
                sleep:
              </Text>
              <Text size="sm">
                {` `} Pauses execution for a specified number of milliseconds.
              </Text>
            </li>
            <li>
              <Text size="sm" code strong>
                getLocalStorage:
              </Text>
              <Text size="sm">{` `}Retrieves data from local storage.</Text>
            </li>
            <li>
              <Text size="sm" code strong>
                setLocalStorage:
              </Text>
              <Text size="sm">{` `} Stores data in local storage.</Text>
            </li>
            <li>
              <Text size="sm" code strong>
                removeLocalStorage:
              </Text>
              <Text size="sm">{` `}Removes data from local storage.</Text>
            </li>
            <li>
              <Text size="sm" code strong>
                uuid:
              </Text>
              <Text size="sm">
                {` `} Generates a unique identifier of a specified length.
              </Text>
            </li>
          </ul>

          <Title level={4}>Function Descriptions</Title>
          <ul className="marker:text-sky-400 list-decimal pl-6 mb-3">
            <li className="py-3">
              <Title level={5} code className="gap-5">
                cn:
              </Title>
              <Flex direction="col">
                {" "}
                <Text size="sm">
                  {` `}Combines class names into a single string while
                  automatically merging Tailwind CSS classes.
                </Text>
                <Text size="sm" strong>
                  Example:
                </Text>
                <CodeViewer
                  language="javascript"
                  copyable
                  wrapLongLines
                  code={`const buttonClass = cn('btn', 'btn-primary', 'p-4', 'hover:bg-blue-500')`}
                />
              </Flex>
            </li>
            <li className="py-3">
              <Title level={5} code>
                copyToClipboard:
              </Title>
              <Flex direction="col">
                {" "}
                <Text size="sm">
                  {` `}Copies text or numbers to the clipboard and returns a
                  success or failure message.
                </Text>
                <Text size="sm" strong>
                  Example:
                </Text>
                <CodeViewer
                  language="javascript"
                  copyable
                  wrapLongLines
                  code={`copyToClipboard('Hello World').then((message) => console.log(message))`}
                />
              </Flex>
            </li>
            <li className="py-3">
              <Title level={5} code>
                sleep:
              </Title>
              <Flex direction="col">
                {" "}
                <Text size="sm">
                  {` `}Pauses execution for the specified number of
                  milliseconds. Useful in asynchronous operations or delaying
                  actions.
                </Text>
                <Text size="sm" strong>
                  Example:
                </Text>
                <CodeViewer
                  language="javascript"
                  copyable
                  wrapLongLines
                  code={`await sleep(1000) // Pauses execution for 1 second`}
                />
              </Flex>
            </li>
            <li className="py-3">
              <Title level={5} code>
                getLocalStorage:
              </Title>
              <Flex direction="col">
                {" "}
                <Text size="sm">
                  {` `}Retrieves data from local storage using a key. If the key
                  does not exist or there is an error, the initial value is
                  returned.
                </Text>
                <Text size="sm" strong>
                  Example:
                </Text>
                <CodeViewer
                  language="javascript"
                  copyable
                  wrapLongLines
                  code={`const userData = getLocalStorage('user', { name: 'Guest' })`}
                />
              </Flex>
            </li>
            <li className="py-3">
              <Title level={5} code>
                setLocalStorage:
              </Title>
              <Flex direction="col">
                {" "}
                <Text size="sm">
                  {` `}Stores a value in local storage under a specified key.
                  Returns `true` if successful, otherwise `false`.
                </Text>
                <Text size="sm" strong>
                  Example:
                </Text>
                <CodeViewer
                  language="javascript"
                  copyable
                  wrapLongLines
                  code={`const success = setLocalStorage('user', { name: 'John Doe' })`}
                />
              </Flex>
            </li>
            <li className="py-3">
              <Title level={5} code>
                removeLocalStorage:
              </Title>
              <Flex direction="col">
                {" "}
                <Text size="sm">
                  {` `}Removes a value from local storage based on the given
                  key. Returns <Text code>true</Text> if successful, otherwise
                  <Text code>false</Text>.
                </Text>
                <Text size="sm" strong>
                  Example:
                </Text>
                <CodeViewer
                  language="javascript"
                  copyable
                  wrapLongLines
                  code={`const removed = removeLocalStorage('user')`}
                />
              </Flex>
            </li>
            <li className="py-3">
              <Title level={5} code>
                uuid:
              </Title>
              <Flex direction="col">
                {" "}
                <Text size="sm">
                  {` `}Generates a unique string identifier of a given length,
                  defaulting to 32 characters.
                </Text>
                <Text size="sm" strong>
                  Example:
                </Text>
                <CodeViewer
                  language="javascript"
                  copyable
                  wrapLongLines
                  code={`const id = uuid() // Generates a unique 32-character ID
const shortId = uuid(8) // Generates a unique 8-character ID`}
                />
              </Flex>
            </li>
          </ul>
        </Flex>
      </Flex>
    </div>
  );
};

export default UtilitiesPage;
